import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { MessageSquare, Rocket, AlertTriangle, Copy, Check } from "lucide-react";
import MadeByBadge from "@/components/brand/MadeByBadge";

// Mirror of Login.jsx detector — see comment there.
function isEmbeddedWebView() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|FBIOS|FB_IAB|Instagram|TikTok|BytedanceWebview|musical_ly|LinkedInApp|Line\/|MicroMessenger|WhatsApp|Snapchat|; wv\)/i.test(ua);
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError("Devam etmek için Kullanıcı Sözleşmesi ve Gizlilik Politikasını onaylamalısın.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await register(name, email, password);
      if (res?.needs_verification) {
        navigate(`/verify-email?email=${encodeURIComponent(res.email || email)}`, { replace: true });
        return;
      }
      navigate("/app", { replace: true });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full flex items-stretch bg-[#CCFF00]" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-10 border-r-[3px] border-[#0A0A0A] relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#D4B3FF] border-hard shadow-hard flex items-center justify-center">
            <MessageSquare strokeWidth={3} className="w-6 h-6" />
          </div>
          <span className="font-display text-2xl">yappo.</span>
        </div>

        <div>
          <p className="font-display text-6xl xl:text-7xl leading-[0.95]">
            Hoş <br />
            <span className="bg-[#D4B3FF] px-3 border-hard shadow-hard inline-block rotate-1">
              geldin
            </span>
            <br />
            aramıza.
          </p>
          <p className="mt-6 max-w-md text-[#0A0A0A]/80 font-medium text-lg">
            Grup sohbetleri, birebir konuşmalar ve gerçek zamanlı bildirimler — birkaç saniyede
            hesabını oluştur.
          </p>
        </div>

        <div className="absolute -left-10 bottom-20 w-40 h-40 bg-[#FF9E80] border-hard shadow-hard-lg rotate-6" />
        <div className="absolute right-10 top-32 w-24 h-24 rounded-full bg-white border-hard shadow-hard" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border-hard shadow-hard-lg rounded-2xl p-8 slide-up">
          <div className="flex items-center gap-2 mb-2">
            <Rocket strokeWidth={3} className="w-5 h-5" />
            <span className="uppercase tracking-widest text-xs font-black">Yeni hesap</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-[0.95] mb-6">
            Sohbete <br /> katıl.
          </h1>

          <button
            type="button"
            onClick={handleGoogle}
            data-testid="google-register-btn"
            className="w-full bg-white text-[#0A0A0A] font-bold border-hard shadow-hard rounded-xl px-5 py-3 flex items-center justify-center gap-3 press-btn"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.4 0-9.9-3.4-11.5-8.2l-6.5 5C9.4 39.6 16.1 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2C41.4 35.5 44 30.2 44 24c0-1.2-.1-2.4-.4-3.5z" />
            </svg>
            Google ile devam et
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-[2px] bg-[#0A0A0A]" />
            <span className="text-xs font-bold uppercase tracking-widest">veya</span>
            <div className="flex-1 h-[2px] bg-[#0A0A0A]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs font-black uppercase tracking-widest" htmlFor="name">
                İsmin
              </label>
              <input
                id="name"
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-3 focus:outline-none focus:bg-white"
                placeholder="Ada Lovelace"
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-widest" htmlFor="email">
                E-posta
              </label>
              <input
                id="email"
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-3 focus:outline-none focus:bg-white"
                placeholder="ornek@yappo.app"
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-widest" htmlFor="password">
                Şifre
              </label>
              <input
                id="password"
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border-hard rounded-xl px-4 py-3 focus:outline-none focus:bg-white"
                placeholder="Güçlü bir şifre"
              />
            </div>

            {error && (
              <div
                data-testid="register-error"
                className="text-sm font-bold bg-[#FF9E80] border-hard-2 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <label className="flex items-start gap-2.5 text-xs text-[#333] leading-snug cursor-pointer">
              <input
                type="checkbox"
                data-testid="register-accept-terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 shrink-0 border-hard-2 rounded accent-[#D4B3FF]"
              />
              <span>
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-black underline">Kullanıcı Sözleşmesi</Link>
                {" "}ve{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-black underline">Gizlilik Politikası</Link>
                &apos;nı okudum, kabul ediyorum.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting || !acceptedTerms}
              data-testid="register-submit-btn"
              className="w-full bg-[#D4B3FF] text-[#0A0A0A] font-black border-hard shadow-hard rounded-xl px-5 py-3 press-btn disabled:opacity-60"
            >
              {submitting ? "Kaydolunuyor…" : "Hesap Oluştur"}
            </button>
          </form>

          <p className="mt-6 text-sm">
            Zaten üye misin?{" "}
            <Link data-testid="go-login-link" to="/login" className="font-black underline underline-offset-4 decoration-4 decoration-[#D4B3FF]">
              Giriş yap
            </Link>
          </p>
          <div className="mt-8 flex justify-center">
            <MadeByBadge />
          </div>
        </div>
      </div>
    </div>
  );
}
